import Box from '@mui/material/Box';
import { tabClasses } from '@mui/material/Tab';
import Tabs, { type TabsProps } from '@mui/material/Tabs';
import TabScrollButton from '@mui/material/TabScrollButton';
import useMediaQuery from '@mui/material/useMediaQuery';
import { useCallback, useEffect, useLayoutEffect, useRef, useState, type ReactElement } from 'react';

import { restingScrollLeft, tabPaddingAdjustment } from '../lib/tabStripPeek';

const FADE_WIDTH_PX = 48;
const TAB_PADDING_X_PX = 16;

type ScrollEdges = { start: boolean; end: boolean };

export type ScrollableTabsProps = Pick<TabsProps, 'value' | 'onChange' | 'aria-label' | 'children'>;

function tabsIn(scroller: HTMLElement): HTMLElement[] {
    return Array.from(scroller.querySelectorAll<HTMLElement>('[role="tab"]'));
}

function widthsOf(tabs: readonly HTMLElement[]): number[] {
    return tabs.map((tab) => tab.getBoundingClientRect().width);
}

function paintFades(scroller: HTMLElement): ScrollEdges {
    const hiddenStart = Math.max(0, scroller.scrollLeft);
    const hiddenEnd = Math.max(0, scroller.scrollWidth - scroller.clientWidth - scroller.scrollLeft);
    scroller.style.setProperty('--tabs-fade-start', `${Math.min(hiddenStart, FADE_WIDTH_PX)}px`);
    scroller.style.setProperty('--tabs-fade-end', `${Math.min(hiddenEnd, FADE_WIDTH_PX)}px`);
    return { start: hiddenStart > 1, end: hiddenEnd > 1 };
}

function fitTabSpacing(scroller: HTMLElement, currentAdjustment: number): number {
    const baseWidths = widthsOf(tabsIn(scroller)).map((width) => width - 2 * currentAdjustment);
    const adjustment = tabPaddingAdjustment(baseWidths, scroller.getBoundingClientRect().width, TAB_PADDING_X_PX);
    scroller.style.setProperty('--tab-padding-adjustment', `${adjustment}px`);
    return adjustment;
}

function scrollToSelectedTab(scroller: HTMLElement, adjustment: number, behavior: ScrollBehavior): void {
    const tabs = tabsIn(scroller);
    const selectedIndex = tabs.findIndex((tab) => tab.getAttribute('aria-selected') === 'true');
    if (selectedIndex === -1) {
        return;
    }
    const left = restingScrollLeft(
        widthsOf(tabs),
        scroller.getBoundingClientRect().width,
        TAB_PADDING_X_PX + adjustment,
        selectedIndex,
    );
    scroller.scrollTo({ left, behavior });
}

export function ScrollableTabs({
    value,
    onChange,
    'aria-label': ariaLabel,
    children,
}: ScrollableTabsProps): ReactElement {
    const [scroller, setScroller] = useState<HTMLDivElement | null>(null);
    const [edges, setEdges] = useState<ScrollEdges>({ start: false, end: false });
    const paddingAdjustmentRef = useRef(0);
    const hasPositionedRef = useRef(false);
    const isWide = useMediaQuery((theme) => theme.breakpoints.up('sm'), { noSsr: true });
    const hasFinePointer = useMediaQuery('(hover: hover) and (pointer: fine)', { noSsr: true });

    const syncEdges = useCallback((element: HTMLElement): void => {
        const next = paintFades(element);
        setEdges((previous) => (previous.start === next.start && previous.end === next.end ? previous : next));
    }, []);

    useLayoutEffect(() => {
        if (scroller === null) {
            return;
        }
        const observer = new ResizeObserver(() => {
            paddingAdjustmentRef.current = fitTabSpacing(scroller, paddingAdjustmentRef.current);
            scrollToSelectedTab(scroller, paddingAdjustmentRef.current, 'instant');
            syncEdges(scroller);
        });
        observer.observe(scroller);
        for (const child of scroller.children) {
            observer.observe(child);
        }
        return (): void => observer.disconnect();
    }, [scroller, syncEdges]);

    useEffect(() => {
        if (scroller === null) {
            return;
        }
        scrollToSelectedTab(scroller, paddingAdjustmentRef.current, hasPositionedRef.current ? 'auto' : 'instant');
        hasPositionedRef.current = true;
    }, [value, scroller]);

    const showPageButtons = scroller !== null && isWide && hasFinePointer && (edges.start || edges.end);

    return (
        <Box sx={{ display: 'flex' }}>
            {showPageButtons ? (
                <TabScrollButton
                    direction="left"
                    orientation="horizontal"
                    disabled={!edges.start}
                    onClick={() => scroller.scrollBy({ left: 2 * FADE_WIDTH_PX - scroller.clientWidth })}
                />
            ) : null}
            <Box
                ref={setScroller}
                onScroll={(event) => syncEdges(event.currentTarget)}
                onFocus={(event) => event.target.scrollIntoView({ block: 'nearest', inline: 'nearest' })}
                sx={{
                    flex: '1 1 auto',
                    minWidth: 0,
                    overflowX: 'auto',
                    overflowY: 'hidden',
                    overscrollBehaviorX: 'contain',
                    scrollBehavior: 'smooth',
                    scrollPaddingInline: `${FADE_WIDTH_PX}px`,
                    scrollbarWidth: 'none',
                    '&::-webkit-scrollbar': { display: 'none' },
                    '@media (prefers-reduced-motion: reduce)': { scrollBehavior: 'auto' },
                    '--tabs-fade-start': '0px',
                    '--tabs-fade-end': '0px',
                    '--tab-padding-adjustment': '0px',
                    maskImage:
                        'linear-gradient(to right, transparent, #000 var(--tabs-fade-start), #000 calc(100% - var(--tabs-fade-end)), transparent)',
                }}>
                <Tabs
                    value={value}
                    onChange={onChange}
                    aria-label={ariaLabel}
                    sx={{
                        width: 'max-content',
                        [`& .${tabClasses.root}`]: {
                            minWidth: 0,
                            maxWidth: 'none',
                            paddingInline: `calc(${TAB_PADDING_X_PX}px + var(--tab-padding-adjustment))`,
                        },
                    }}>
                    {children}
                </Tabs>
            </Box>
            {showPageButtons ? (
                <TabScrollButton
                    direction="right"
                    orientation="horizontal"
                    disabled={!edges.end}
                    onClick={() => scroller.scrollBy({ left: scroller.clientWidth - 2 * FADE_WIDTH_PX })}
                />
            ) : null}
        </Box>
    );
}
